/**
 * MailboxListView Component
 * Paginated, searchable list of mailboxes for the EdgeMail Management Console
 */
import React, { useState, useCallback, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  FunnelIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  PauseCircleIcon,
  ArrowPathIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import { useMailboxList, useDomains, useSuspendMailbox, useReactivateMailbox } from '../../hooks/useMailboxes';
import type { Mailbox, MailboxFilters, Domain } from '../../types/admin.types';

// Format bytes to human-readable
function formatBytes(bytes: number | undefined | null): string {
  if (bytes === undefined || bytes === null || isNaN(bytes) || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// Format date
function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface MailboxListViewProps {
  onCreateMailbox?: () => void;
  onEditMailbox?: (mailbox: Mailbox) => void;
  onViewMailbox?: (mailbox: Mailbox) => void;
}

export function MailboxListView({
  onCreateMailbox,
  onEditMailbox,
  onViewMailbox,
}: MailboxListViewProps) {
  // Filter state
  const [filters, setFilters] = useState<MailboxFilters>({
    search: '',
    status: 'all',
    sortBy: 'email',
    sortOrder: 'asc',
    page: 1,
    pageSize: 20,
  });

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMailboxes, setSelectedMailboxes] = useState<Set<string>>(new Set());

  // Queries
  const { data, isLoading, isError, refetch } = useMailboxList(filters);
  const { data: domains } = useDomains();

  // Mutations
  const suspendMutation = useSuspendMailbox();
  const reactivateMutation = useReactivateMailbox();

  // Handlers
  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }));
  }, []);

  const handleStatusFilter = useCallback((status: MailboxFilters['status']) => {
    setFilters((prev) => ({ ...prev, status, page: 1 }));
  }, []);

  const handleDomainFilter = useCallback((domainId: string) => {
    setFilters((prev) => ({ ...prev, domainId: domainId || undefined, page: 1 }));
  }, []);

  const handleSort = useCallback((sortBy: MailboxFilters['sortBy']) => {
    setFilters((prev) => ({
      ...prev,
      sortBy,
      sortOrder: prev.sortBy === sortBy && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handleSelectAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked && data?.mailboxes) {
        setSelectedMailboxes(new Set(data.mailboxes.map((m) => m.mailbox_id)));
      } else {
        setSelectedMailboxes(new Set());
      }
    },
    [data?.mailboxes]
  );

  const handleSelectMailbox = useCallback((mailboxId: string) => {
    setSelectedMailboxes((prev) => {
      const next = new Set(prev);
      if (next.has(mailboxId)) {
        next.delete(mailboxId);
      } else {
        next.add(mailboxId);
      }
      return next;
    });
  }, []);

  const handleSuspend = useCallback(
    async (mailboxId: string) => {
      await suspendMutation.mutateAsync(mailboxId);
    },
    [suspendMutation]
  );

  const handleReactivate = useCallback(
    async (mailboxId: string) => {
      await reactivateMutation.mutateAsync(mailboxId);
    },
    [reactivateMutation]
  );

  // Computed values
  const sortIcon = useMemo(() => {
    return filters.sortOrder === 'asc' ? (
      <ChevronUpIcon className="h-4 w-4" />
    ) : (
      <ChevronDownIcon className="h-4 w-4" />
    );
  }, [filters.sortOrder]);

  const isAllSelected = useMemo(() => {
    return data?.mailboxes && data.mailboxes.length > 0 && selectedMailboxes.size === data.mailboxes.length;
  }, [data?.mailboxes, selectedMailboxes.size]);

  // Render helpers
  const renderStatusBadge = (mailbox: Mailbox) => {
    if (mailbox.is_active) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
          <CheckCircleIcon className="h-3.5 w-3.5" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
        <PauseCircleIcon className="h-3.5 w-3.5" />
        Suspended
      </span>
    );
  };

  const renderQuotaBar = (mailbox: Mailbox) => {
    const usedBytes = mailbox.used_bytes || 0;
    const quotaBytes = mailbox.quota_bytes || 0;
    const percentUsed = quotaBytes > 0 ? (usedBytes / quotaBytes) * 100 : 0;
    const isWarning = percentUsed >= 75;
    const isCritical = percentUsed >= 90;

    return (
      <div className="w-full">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-gray-600 dark:text-gray-400">
            {formatBytes(usedBytes)} / {formatBytes(quotaBytes)}
          </span>
          <span
            className={`font-medium ${
              isCritical
                ? 'text-red-600 dark:text-red-400'
                : isWarning
                ? 'text-yellow-600 dark:text-yellow-400'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            {percentUsed.toFixed(0)}%
          </span>
        </div>
        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${
              isCritical
                ? 'bg-red-500'
                : isWarning
                ? 'bg-yellow-500'
                : 'bg-lex-accent'
            }`}
            style={{ width: `${Math.min(percentUsed, 100)}%` }}
          />
        </div>
      </div>
    );
  };

  const renderSortableHeader = (label: string, sortKey: MailboxFilters['sortBy']) => (
    <button
      onClick={() => handleSort(sortKey)}
      className="flex items-center gap-1 font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
    >
      {label}
      {filters.sortBy === sortKey && sortIcon}
    </button>
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent" />
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-red-500">
        <ExclamationCircleIcon className="h-12 w-12 mb-4" />
        <p>Failed to load mailboxes</p>
        <button
          onClick={() => refetch()}
          className="mt-4 px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const mailboxes = data?.mailboxes || [];
  const totalPages = data?.totalPages || 1;
  const currentPage = data?.page || 1;
  const total = data?.total || 0;

  return (
    <div className="space-y-4">
      {/* Header with Search and Actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-1 gap-3 items-center w-full sm:w-auto">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search mailboxes..."
              value={filters.search}
              onChange={handleSearch}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-lex-accent focus:border-transparent"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg border ${
              showFilters
                ? 'bg-lex-accent-soft border-lex-accent text-lex-accent'
                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
          </button>

          {/* Refresh */}
          <button
            onClick={() => refetch()}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ArrowPathIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Create Button */}
        {onCreateMailbox && (
          <button
            onClick={onCreateMailbox}
            className="flex items-center gap-2 px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            New Mailbox
          </button>
        )}
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="flex flex-wrap gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          {/* Status Filter */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Status</label>
            <select
              value={filters.status || 'all'}
              onChange={(e) => handleStatusFilter(e.target.value as MailboxFilters['status'])}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          {/* Domain Filter */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Domain</label>
            <select
              value={filters.domainId || ''}
              onChange={(e) => handleDomainFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            >
              <option value="">All Domains</option>
              {domains?.map((domain: Domain) => (
                <option key={domain.domain_id} value={domain.domain_id}>
                  {domain.domain}
                </option>
              ))}
            </select>
          </div>

          {/* Results count */}
          <div className="flex items-end ml-auto">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {total} mailbox{total !== 1 ? 'es' : ''} found
            </span>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 dark:border-gray-600 text-lex-accent focus:ring-lex-accent"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {renderSortableHeader('Email', 'email')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {renderSortableHeader('Display Name', 'display_name')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider min-w-[200px]">
                {renderSortableHeader('Storage', 'used_bytes')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {renderSortableHeader('Last Login', 'last_login_at')}
              </th>
              <th className="w-12 px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {mailboxes.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center">
                  <EnvelopeIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">No mailboxes found</p>
                  {onCreateMailbox && (
                    <button
                      onClick={onCreateMailbox}
                      className="mt-4 text-lex-accent hover:text-lex-accent-hover font-medium"
                    >
                      Create your first mailbox
                    </button>
                  )}
                </td>
              </tr>
            ) : (
              mailboxes.map((mailbox) => (
                <tr
                  key={mailbox.mailbox_id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                  onClick={() => onViewMailbox?.(mailbox)}
                >
                  <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedMailboxes.has(mailbox.mailbox_id)}
                      onChange={() => handleSelectMailbox(mailbox.mailbox_id)}
                      className="rounded border-gray-300 dark:border-gray-600 text-lex-accent focus:ring-lex-accent"
                    />
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 h-10 w-10 bg-lex-accent-soft rounded-full flex items-center justify-center">
                        <EnvelopeIcon className="h-5 w-5 text-lex-accent" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{mailbox.email}</p>
                        {mailbox.domain && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">{mailbox.domain.domain}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-gray-700 dark:text-gray-300">
                    {mailbox.display_name || '-'}
                  </td>
                  <td className="px-4 py-4">{renderStatusBadge(mailbox)}</td>
                  <td className="px-4 py-4">{renderQuotaBar(mailbox)}</td>
                  <td className="px-4 py-4 text-gray-600 dark:text-gray-400 text-sm">
                    {formatDate(mailbox.last_login_at)}
                  </td>
                  <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger>
                        <EllipsisVerticalIcon className="h-5 w-5 text-gray-500" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEditMailbox?.(mailbox)}>
                          <PencilIcon className="h-4 w-4 mr-2" />
                          Edit Settings
                        </DropdownMenuItem>
                        {mailbox.is_active ? (
                          <DropdownMenuItem
                            onClick={() => handleSuspend(mailbox.mailbox_id)}
                            disabled={suspendMutation.isPending}
                            className="text-yellow-600 dark:text-yellow-400"
                          >
                            <PauseCircleIcon className="h-4 w-4 mr-2" />
                            Suspend Mailbox
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => handleReactivate(mailbox.mailbox_id)}
                            disabled={reactivateMutation.isPending}
                            className="text-green-600 dark:text-green-400"
                          >
                            <CheckCircleIcon className="h-4 w-4 mr-2" />
                            Reactivate Mailbox
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {(currentPage - 1) * (filters.pageSize || 20) + 1} to{' '}
            {Math.min(currentPage * (filters.pageSize || 20), total)} of {total} results
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-1 rounded-lg text-sm ${
                    currentPage === pageNum
                      ? 'bg-lex-accent text-lex-accent-contrast'
                      : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MailboxListView;
